import { i18n } from '@/services/i18n';
import { Component } from 'vue-property-decorator';

export interface ErrorDetails {
  code: string;
  message: string;
  details?: any;
  timestamp: Date;
}

export interface UserFriendlyError {
  title: string;
  message: string;
  actionable: boolean;
  retryable: boolean;
  suggestions?: string[];
}

export class AutoApprovalErrorHandler {
  private static get errorMessages(): { [key: string]: UserFriendlyError } {
    const t = (key: string) => i18n.t(key) as string;

    return {
      NETWORK_ERROR: {
        title: t('autoApproval.errorTitles.connectionProblem'),
        message: t('autoApproval.errors.networkError'),
        actionable: true,
        retryable: true,
        suggestions: [
          t('autoApproval.recommendations.checkInternet'),
          t('autoApproval.recommendations.refreshPage'),
          t('autoApproval.recommendations.contactAdmin'),
        ]
      },
      VALIDATION_ERROR: {
        title: t('autoApproval.errorTitles.validationError'),
        message: t('autoApproval.errors.validationError'),
        actionable: true,
        retryable: false,
        suggestions: [
          t('autoApproval.recommendations.checkFields'),
          t('autoApproval.recommendations.checkRanges'),
        ]
      },
      PARAMETER_SAVE_ERROR: {
        title: t('autoApproval.errorTitles.saveParamsError'),
        message: t('autoApproval.errors.saveAutoApprovalParams'),
        actionable: true,
        retryable: true,
        suggestions: [
          t('autoApproval.recommendations.checkData'),
          t('autoApproval.recommendations.trySaveAgain'),
          t('autoApproval.recommendations.contactAdminOnError'),
        ]
      },
      PARAMETER_LOAD_ERROR: {
        title: t('autoApproval.errorTitles.loadParamsError'),
        message: t('autoApproval.errors.loadSavedParams'),
        actionable: true,
        retryable: true,
        suggestions: [
          t('autoApproval.recommendations.refreshPage'),
          t('autoApproval.recommendations.checkParamsSaved'),
        ]
      },
      RULES_EXPORT_ERROR: {
        title: t('autoApproval.errorTitles.exportRulesError'),
        message: t('autoApproval.errors.exportRulesToExcel'),
        actionable: true,
        retryable: true,
        suggestions: [
          t('autoApproval.recommendations.tryExportAgain'),
          t('autoApproval.recommendations.checkDownloadRights'),
        ]
      },
      RULES_IMPORT_ERROR: {
        title: t('autoApproval.errorTitles.importRulesError'),
        message: t('autoApproval.errors.importRulesFromExcel'),
        actionable: true,
        retryable: false,
        suggestions: [
          t('autoApproval.recommendations.checkExcelFormat'),
          t('autoApproval.recommendations.checkFileData'),
          t('autoApproval.recommendations.checkRequiredFields'),
        ]
      },
      PERMISSION_ERROR: {
        title: t('autoApproval.errorTitles.insufficientPermissions'),
        message: t('autoApproval.errors.permissionError'),
        actionable: false,
        retryable: false,
        suggestions: [
          t('autoApproval.recommendations.contactAdminForRights'),
        ]
      },
      UNKNOWN_ERROR: {
        title: t('autoApproval.errorTitles.unknownError'),
        message: t('autoApproval.errors.unexpectedError'),
        actionable: true,
        retryable: true,
        suggestions: [
          t('autoApproval.recommendations.tryAgain'),
          t('autoApproval.recommendations.refreshPageShort'),
          t('autoApproval.recommendations.contactSupport'),
        ]
      }
    };
  }

  /**
   * Обрабатывает ошибку и возвращает пользовательски-дружественное сообщение
   */
  public static handleError(error: any): UserFriendlyError {
    const errorDetails = this.parseError(error);
    
    // Определяем тип ошибки
    let errorType = 'UNKNOWN_ERROR';
    
    if (errorDetails.code) {
      errorType = errorDetails.code;
    } else if (error.response) {
      // HTTP ошибки
      if (error.response.status === 403) {
        errorType = 'PERMISSION_ERROR';
      } else if (error.response.status >= 400 && error.response.status < 500) {
        errorType = 'VALIDATION_ERROR';
      } else if (error.response.status >= 500) {
        errorType = 'PARAMETER_SAVE_ERROR';
      }
    } else if (error.request) {
      // Ошибки сети
      errorType = 'NETWORK_ERROR';
    }

    const userError = this.errorMessages[errorType] || this.errorMessages.UNKNOWN_ERROR;
    
    // Логируем техническую информацию для разработчиков
    console.error('AutoApproval Error:', {
      type: errorType,
      original: error,
      details: errorDetails,
      userMessage: userError
    });

    return userError;
  }

  /**
   * Парсит ошибку и извлекает детали
   */
  private static parseError(error: any): ErrorDetails {
    return {
      code: error.code || (error.response && error.response.data && error.response.data.code) || '',
      message: error.message || (error.response && error.response.data && error.response.data.message) || 'Unknown error',
      details: (error.response && error.response.data) || error.details,
      timestamp: new Date()
    };
  }

  /**
   * Создает сообщение для Toast уведомления
   */
  public static createToastMessage(error: UserFriendlyError, type: 'error' | 'warning' = 'error'): any {
    return {
      type,
      title: error.title,
      message: error.message,
      duration: error.retryable ? 5000 : 8000, // Больше времени для не-повторяемых ошибок
      showClose: true
    };
  }

  /**
   * Создает детальное сообщение с предложениями
   */
  public static createDetailedMessage(error: UserFriendlyError): string {
    let message = error.message;
    
    if (error.suggestions && error.suggestions.length > 0) {
      message += '\n\n' + i18n.t('autoApproval.text.recommendations') as string + ':\n';
      error.suggestions.forEach((suggestion, index) => {
        message += `${index + 1}. ${suggestion}\n`;
      });
    }
    
    return message;
  }

  /**
   * Определяет, можно ли повторить операцию
   */
  public static isRetryable(error: UserFriendlyError): boolean {
    return error.retryable;
  }

  /**
   * Определяет, требует ли ошибка действий пользователя
   */
  public static isActionable(error: UserFriendlyError): boolean {
    return error.actionable;
  }
}
